import { BakingProductionLog } from '../types/bakingProductionLog';
import { TraceCode } from '../types/traceCode';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

interface CreateBakingLogResponse {
  log: BakingProductionLog;
  batchRecordId: string;
  traceCode: TraceCode;
}

interface DeleteBakingLogResponse {
  batchRecordId: string | null;
  traceCodeId: string | null;
}

const bakingProductionLogApiService = {
  /**
   * Fetch all baking production logs
   */
  fetchBakingProductionLogs: async (): Promise<BakingProductionLog[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/baking-production-log`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch baking production logs');
      }

      return data.logs;
    } catch (error) {
      console.error('Error fetching baking production logs from API:', error);
      throw error;
    }
  },

  /**
   * Create a new baking production log
   */
  createBakingProductionLog: async (log: BakingProductionLog): Promise<CreateBakingLogResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/baking-production-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(log),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create baking production log');
      }

      return { log: data.log, batchRecordId: data.batchRecordId, traceCode: data.traceCode };
    } catch (error) {
      console.error('Error creating baking production log via API:', error);
      throw error;
    }
  },

  /**
   * Update an existing baking production log
   */
  updateBakingProductionLog: async (log: BakingProductionLog): Promise<BakingProductionLog> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/baking-production-log/${log.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(log),
        }
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update baking production log');
      }

      return data.log;
    } catch (error) {
      console.error('Error updating baking production log via API:', error);
      throw error;
    }
  },

  /**
   * Delete a baking production log
   */
  deleteBakingProductionLog: async (id: string): Promise<DeleteBakingLogResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/baking-production-log/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete baking production log');
      }

      return {
        batchRecordId: data.batchRecordId || null,
        traceCodeId: data.traceCodeId || null,
      };
    } catch (error) {
      console.error('Error deleting baking production log via API:', error);
      throw error;
    }
  },

  /**
   * Generate the next lot number for a given product code and date
   * Format: {productCode}{YYYYMMDD}{##}
   */
  generateLotNumber: async (productCode: string, date: string): Promise<string> => {
    try {
      const response = await fetch(`${API_BASE_URL}/baking-production-log/generate-lot-number`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productCode, date }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to generate lot number');
      }

      return data.lotNumber;
    } catch (error) {
      console.error('Error generating lot number via API:', error);
      throw error;
    }
  },
};

export default bakingProductionLogApiService;
